import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AIProductResearchDialog from "./AIProductResearchDialog";
import AIActiveMarketDialog from "./AIActiveMarketDialog";
import AIPlayabilityDialog from "./AIPlayabilityDialog";
import AIBusinessAnalysisDialog from "./AIBusinessAnalysisDialog";
import AICommercialAssessmentDialog from "./AICommercialAssessmentDialog";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from "@mui/material";

// FC266: five independently saved research stages; commercial assessments never execute listing actions.
const STAGES = [
  ["ebay_product_research", "eBay Product Research", "Guided form", "FC262"],
  ["active_market", "Active Market", "Guided + AI", "FC263"],
  ["tournament_playability", "Tournament Playability", "Autonomous", "FC264"],
  ["cammez_business_analysis", "Cammez Business Analysis", "Internal", "FC265"],
  ["commercial_assessment", "Commercial Assessment", "AI + calculations", "FC266"],
];
const CHANGED_FIELDS = ["listing_price", "ebay_available_quantity", "listing_status",
  "ebay_listing_status", "fc256_verified_finish", "sku", "ebay_listing_id", "ebay_offer_id"];
const val = (value, missing = "Not recorded") => value === null || value === undefined ||
  String(value).trim() === "" ? missing : String(value);
const finish = (value) => {
  const text = val(value, "");
  return text && !["unknown", "none", "null"].includes(text.toLowerCase())
    ? text : "Unknown / needs verification";
};
const qty = (value) => value === null || value === undefined || String(value).trim() === ""
  ? "Not recorded" : val(value);
const money = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") return "Not recorded";
  const number = Number(value);
  return Number.isFinite(number)
    ? number.toLocaleString("en-AU", { style: "currency", currency: "AUD" })
    : "Invalid stored price";
};
const identity = (value) => value === null || value === undefined ? null : String(value);
const cleanStatus = (value) => val(value, "NOT_STARTED").replaceAll("_", " ");

function Info({ label, value }) {
  return <Box sx={{ minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{value}</Typography>
  </Box>;
}

export default function AIResearchWorkspace({ listing, listingId, onBack, reloadListings, listingsLoading, listingsError }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [refreshSeq, setRefreshSeq] = useState(0);
  const [productResearchOpen, setProductResearchOpen] = useState(false);
  const [activeMarketOpen, setActiveMarketOpen] = useState(false);
  const [playabilityOpen, setPlayabilityOpen] = useState(false);
  const [businessOpen, setBusinessOpen] = useState(false);
  const [assessmentOpen, setAssessmentOpen] = useState(false);

  useEffect(() => {
    if (!listingId) return undefined;
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");
    axios.get("/api/ai-operator/research-runs", { params: { listing_id: listingId }, signal: controller.signal })
      .then(({ data }) => {
        if (!active) return;
        if (data?.ok !== true || !Array.isArray(data.runs)) throw new Error("Unexpected research-run response");
        setRuns(data.runs);
      })
      .catch((err) => {
        if (!active || axios.isCancel(err)) return;
        setRuns([]);
        setError(err?.response?.data?.error || err?.message || "Could not load research runs");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [listingId, refreshSeq]);

  const activeRun = useMemo(() => runs.find((r) => r.status === "IN_PROGRESS") || null, [runs]);
  const lastRun = activeRun || runs[0] || null;
  const snapshot = lastRun?.snapshot_json || null;
  const changedFields = useMemo(() => {
    if (!listing || !snapshot) return [];
    return CHANGED_FIELDS.filter((key) => {
      const a = snapshot[key]; const b = listing[key];
      if (a === null || a === undefined || a === "") return !(b === null || b === undefined || b === "");
      if (b === null || b === undefined || b === "") return true;
      if (["listing_price", "ebay_available_quantity"].includes(key)) {
        const nA = Number(a); const nB = Number(b);
        if (Number.isFinite(nA) && Number.isFinite(nB)) return nA !== nB;
      }
      return String(a) !== String(b);
    });
  }, [listing, snapshot]);

  const action = async (url, body, message) => {
    if (busy) return;
    setBusy(true);
    setError(""); setNotice("");
    try {
      const { data } = await axios.post(url, body);
      if (data?.ok !== true || !data.run) throw new Error("The server did not confirm saving the research run");
      setNotice(message);
      setRefreshSeq((prev) => prev + 1);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Research run was not saved");
    } finally { setBusy(false); }
  };

  return <Box>
    <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} justifyContent="space-between" gap={1.5} sx={{ mb: 2 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>AI Operator — Research Workspace</Typography>
        <Typography variant="body2" color="text.secondary">Single stored listing #{listingId}; guided SOLD/ACTIVE, playability, internal business analysis and research-only assessment.</Typography>
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Button variant="outlined" onClick={onBack}>Back to listings</Button>
        <Button variant="outlined" onClick={() => { reloadListings(); setRefreshSeq((n) => n + 1); }} disabled={listingsLoading || loading || busy}>Refresh stored data</Button>
      </Stack>
    </Stack>
    <Alert severity="info" sx={{ mb: 2 }}>SOLD and ACTIVE use guided eBay forms. Tournament Playability lets Codex independently investigate permitted sources, then enter evidence manually. Commercial Assessment captures evidence-backed recommendations for review only. No listing or eBay changes can be made here.</Alert>
    {listingsError && <Alert severity="error" sx={{ mb: 2 }}>Listing refresh failed: {listingsError}</Alert>}
    {!listingsLoading && !listing && <Alert severity="error" sx={{ mb: 2 }}>Local listing #{listingId} is missing or unavailable. The research workspace cannot start a new run.</Alert>}
    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
    {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Current stored listing</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
        <Info label="Local listing ID" value={listingId} />
        <Info label="Card name" value={val(listing?.card_name)} />
        <Info label="Listing title" value={val(listing?.listing_title)} />
        <Info label="Set" value={val(listing?.card_set)} />
        <Info label="SKU" value={val(listing?.sku)} />
        <Info label="Verified finish" value={finish(listing?.fc256_verified_finish)} />
        <Info label="Condition (stored)" value={val(listing?.listing_condition)} />
        <Info label="Price (stored)" value={money(listing?.listing_price)} />
        <Info label="eBay available qty (stored)" value={qty(listing?.ebay_available_quantity)} />
        <Info label="Local status" value={val(listing?.listing_status)} />
        <Info label="eBay status (stored)" value={val(listing?.ebay_listing_status)} />
        <Info label="Last eBay sync" value={val(listing?.last_ebay_sync_at, "Not recorded — freshness unknown")} />
        <Info label="eBay listing ID" value={val(listing?.ebay_listing_id)} />
        <Info label="eBay offer ID" value={val(listing?.ebay_offer_id)} />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>Fields are app-stored, not live eBay verification. Missing verified finish must not be inferred.</Typography>
    </Paper>
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={1} alignItems={{ sm: "center" }} justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Research run</Typography>
          <Typography variant="body2" color="text.secondary">One open run per local listing, older closed runs retained.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {!activeRun && listing && !listingsLoading && !loading && !error &&
            <Button variant="contained" disabled={busy} onClick={() => action("/api/ai-operator/research-runs", { listing_id: Number(listingId) }, "Research run created. No research has been performed.")}>Start research run</Button>}
          {activeRun && <Button variant="outlined" color="warning" disabled={busy || loading} onClick={() => {
            if (window.confirm(`Close research run #${activeRun.id}? This preserves its history but allows starting another run.`)) {
              action(`/api/ai-operator/research-runs/${activeRun.id}/close`, {}, "Research run closed; history retained.");
            }
          }}>Close run</Button>}
        </Stack>
      </Stack>
      {loading && <Stack direction="row" alignItems="center" spacing={1}><CircularProgress size={18} /><Typography>Loading saved research state…</Typography></Stack>}
      {!loading && !lastRun && !error && <Alert severity="info">No saved research run exists yet. Start one to save its listing snapshot and the five planned stage records.</Alert>}
      {!loading && lastRun && <>
        <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" sx={{ my: 1 }}>
          <Chip label={`Run #${lastRun.id}`} color="primary" variant="outlined" />
          <Chip label={lastRun.status === "IN_PROGRESS" ? "Open — research enabled" : "Closed — historical"} variant="outlined" />
          <Typography variant="caption" color="text.secondary">Snapshot created: {val(lastRun.created_at)}</Typography>
          <Typography variant="caption" color="text.secondary">Saved runs: {runs.length}</Typography>
        </Stack>
        {changedFields.length > 0 && <Alert severity="warning" sx={{ my: 1 }}>The stored listing differs from this run's original snapshot: {changedFields.join(", ")}. Review identity and freshness before future research.</Alert>}
        {!listing && <Alert severity="warning">Listing no longer exists in the current listing response; saved snapshot remains for history.</Alert>}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Original snapshot (immutable)</Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 2, mb: 2 }}>
          <Info label="Local listing ID" value={val(snapshot?.id)} />
          <Info label="SKU at start" value={val(snapshot?.sku)} />
          <Info label="Price at start" value={money(snapshot?.listing_price)} />
          <Info label="Quantity at start" value={qty(snapshot?.ebay_available_quantity)} />
          <Info label="Finish at start" value={finish(snapshot?.fc256_verified_finish)} />
          <Info label="Local status at start" value={val(snapshot?.listing_status)} />
          <Info label="eBay status at start" value={val(snapshot?.ebay_listing_status)} />
          <Info label="eBay ID at start" value={val(snapshot?.ebay_listing_id)} />
        </Box>
      </>}
      {!loading && !activeRun && runs.length > 0 && <Typography variant="body2" color="text.secondary">The latest run is closed; you can start a new run to capture a new snapshot.</Typography>}
    </Paper>
    <Paper variant="outlined" sx={{ minWidth: 0, overflow: "hidden" }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Research stages</Typography>
        <Typography variant="body2" color="text.secondary">SOLD Product Research, ACTIVE competitors, and Tournament Playability have separate evidence histories. Business Analysis reads Cammez history; Commercial Assessment is research-only and never executes changes.</Typography>
      </Box>
      <TableContainer>
        <Table size="small" sx={{ minWidth: 650 }} aria-label="AI Operator planned research stages">
          <TableHead><TableRow><TableCell>Stage</TableCell><TableCell>Method</TableCell><TableCell>Recorded state</TableCell><TableCell>Last saved</TableCell><TableCell>Action</TableCell></TableRow></TableHead>
          <TableBody>{STAGES.map(([key, label, method, future]) => {
            const stage = lastRun?.stages?.[key];
            return <TableRow key={key}><TableCell sx={{ fontWeight: 600 }}>{label}</TableCell>
              <TableCell>{method}</TableCell><TableCell>{stage ? cleanStatus(stage.status) : "NOT STARTED"}</TableCell>
              <TableCell>{val(stage?.updated_at, "—")}</TableCell>
              <TableCell>{key === "ebay_product_research" && lastRun ?
                <Button size="small" variant="outlined" disabled={busy || loading} onClick={() => setProductResearchOpen(true)}>
                  {lastRun.status === "IN_PROGRESS" ? "Enter Research Data" : "View Research"}
                </Button> : key === "active_market" && lastRun ?
                <Button size="small" variant="outlined" disabled={busy || loading} onClick={() => setActiveMarketOpen(true)}>
                  {lastRun.status === "IN_PROGRESS" ? "Enter Active Research" : "View Active Research"}
                </Button> : key === "tournament_playability" && lastRun ?
                <Button size="small" variant="outlined" disabled={busy || loading} onClick={() => setPlayabilityOpen(true)}>
                  {lastRun.status === "IN_PROGRESS" ? "Enter Playability Research" : "View Playability"}
                </Button> : key === "cammez_business_analysis" && lastRun ?
                <Button size="small" variant="outlined" disabled={busy || loading} onClick={() => setBusinessOpen(true)}>
                  {lastRun.status === "IN_PROGRESS" ? "Load Business Analysis" : "View Business Analysis"}
                </Button> : key === "commercial_assessment" && lastRun ?
                <Button size="small" variant="outlined" disabled={busy || loading} onClick={() => setAssessmentOpen(true)}>
                  {lastRun.status === "IN_PROGRESS" ? "Enter Assessment" : "View Assessment"}
                </Button> :
                <Button disabled size="small" variant="outlined" aria-label={`${label} planned for ${future}`}>Planned — {future}</Button>}
              </TableCell></TableRow>;
          })}</TableBody>
        </Table>
      </TableContainer>
      {runs.length > 1 && <Box sx={{ px: 2, py: 1 }}><Typography variant="caption" color="text.secondary">Earlier closed run IDs: {runs.slice(1).map((r) => r.id).join(", ")}. This screen shows the active or latest run; historical records remain retrievable through the research-run API.</Typography></Box>}
    </Paper>
    {productResearchOpen && lastRun &&
      <AIProductResearchDialog open={productResearchOpen} run={lastRun}
        onClose={() => setProductResearchOpen(false)}
        onSaved={() => setRefreshSeq((n) => n + 1)} />}
    {activeMarketOpen && lastRun &&
      <AIActiveMarketDialog open={activeMarketOpen} run={lastRun}
        onClose={() => setActiveMarketOpen(false)}
        onSaved={() => setRefreshSeq((n) => n + 1)} />}
    {assessmentOpen && lastRun &&
      <AICommercialAssessmentDialog open={assessmentOpen} run={lastRun}
        onClose={() => setAssessmentOpen(false)} onSaved={() => setRefreshSeq((n) => n + 1)} />}
    {businessOpen && lastRun &&
      <AIBusinessAnalysisDialog open={businessOpen} run={lastRun}
        onClose={() => setBusinessOpen(false)} onSaved={() => setRefreshSeq((n) => n + 1)} />}
    {playabilityOpen && lastRun &&
      <AIPlayabilityDialog open={playabilityOpen} run={lastRun}
        onClose={() => setPlayabilityOpen(false)}
        onSaved={() => setRefreshSeq((n) => n + 1)} />}
  </Box>;
}
